// frontend/src/utils/brandingUtils.js

/**
 * Branding utility functions for consistent color and styling application
 */

/**
 * Get branding colors from context
 * @param {Object} branding - The branding context object
 * @returns {Object} - Object with all branding colors
 */
export const getBrandingColors = (branding) => {
  return {
    primary: branding?.primaryColor || '#3B82F6',
    secondary: branding?.secondaryColor || '#10B981',
    accent: branding?.accentColor || '#F59E0B',
    text: branding?.textColor || '#1F2937',
    background: branding?.backgroundColor || '#FFFFFF',
  };
};

/**
 * Get CSS custom properties for branding
 * @param {Object} branding - The branding context object
 * @returns {Object} - CSS custom properties object
 */
export const getBrandingCSSVars = (branding) => {
  const colors = getBrandingColors(branding);
  return {
    '--brand-primary': colors.primary,
    '--brand-secondary': colors.secondary,
    '--brand-accent': colors.accent,
    '--brand-text': colors.text,
    '--brand-background': colors.background,
  };
};

/**
 * Get Tailwind CSS classes for branding colors
 * @param {Object} branding - The branding context object
 * @returns {Object} - Object with Tailwind classes
 */
export const getBrandingClasses = (branding) => {
  return {
    primary: 'text-[var(--brand-primary)]',
    primaryBg: 'bg-[var(--brand-primary)]',
    primaryBorder: 'border-[var(--brand-primary)]',
    primaryHover: 'hover:bg-[var(--brand-primary)]',
    primaryFocus: 'focus:ring-[var(--brand-primary)]',
    
    secondary: 'text-[var(--brand-secondary)]',
    secondaryBg: 'bg-[var(--brand-secondary)]',
    secondaryBorder: 'border-[var(--brand-secondary)]',
    secondaryHover: 'hover:bg-[var(--brand-secondary)]',
    
    accent: 'text-[var(--brand-accent)]',
    accentBg: 'bg-[var(--brand-accent)]',
    accentBorder: 'border-[var(--brand-accent)]',
    accentHover: 'hover:bg-[var(--brand-accent)]',
    
    text: 'text-[var(--brand-text)]',
    background: 'bg-[var(--brand-background)]',
  };
};

/**
 * Get button styling classes based on variant and branding
 * @param {string} variant - Button variant (primary, secondary, accent, outline, ghost)
 * @param {Object} branding - The branding context object
 * @returns {string} - Combined CSS classes
 */
export const getButtonClasses = (variant = 'primary', branding) => {
  const baseClasses = 'inline-flex items-center justify-center rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none';
  
  const variants = {
    primary: 'bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary)]/90 focus:ring-[var(--brand-primary)]',
    secondary: 'bg-[var(--brand-secondary)] text-white hover:bg-[var(--brand-secondary)]/90 focus:ring-[var(--brand-secondary)]',
    accent: 'bg-[var(--brand-accent)] text-white hover:bg-[var(--brand-accent)]/90 focus:ring-[var(--brand-accent)]',
    outline: 'border border-[var(--brand-primary)] text-[var(--brand-primary)] hover:bg-[var(--brand-primary)] hover:text-white focus:ring-[var(--brand-primary)]',
    ghost: 'text-[var(--brand-primary)] hover:bg-[var(--brand-primary)]/10 focus:ring-[var(--brand-primary)]',
    destructive: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
  };
  
  return `${baseClasses} ${variants[variant] || variants.primary}`;
};

/**
 * Get status color classes based on status and branding
 * @param {string} status - Status value
 * @param {string} type - Status type (status, priority, etc.)
 * @param {Object} branding - The branding context object
 * @returns {string} - CSS classes for the status
 */
export const getStatusClasses = (status, type = 'status', branding) => {
  const colors = getBrandingColors(branding || {});
  
  const statusColors = {
    // Task/Client Status
    active: `bg-[${colors.secondary}]/10 text-[${colors.secondary}] border-[${colors.secondary}]/20`,
    completed: `bg-[${colors.secondary}]/10 text-[${colors.secondary}] border-[${colors.secondary}]/20`,
    success: `bg-[${colors.secondary}]/10 text-[${colors.secondary}] border-[${colors.secondary}]/20`,
    
    pending: `bg-[${colors.accent}]/10 text-[${colors.accent}] border-[${colors.accent}]/20`,
    in_progress: `bg-[${colors.primary}]/10 text-[${colors.primary}] border-[${colors.primary}]/20`,
    warning: `bg-[${colors.accent}]/10 text-[${colors.accent}] border-[${colors.accent}]/20`,
    
    prospect: `bg-blue-100 text-blue-700 border-blue-200`,
    paused: `bg-orange-100 text-orange-700 border-orange-200`,
    churned: `bg-red-100 text-red-700 border-red-200`,
    closed_lost: `bg-gray-100 text-gray-700 border-gray-200`,
    inactive: `bg-gray-100 text-gray-700 border-gray-200`,
    archived: `bg-gray-100 text-gray-700 border-gray-200`,
    
    // Priority
    high: `bg-red-100 text-red-700 border-red-200`,
    medium: `bg-[${colors.accent}]/10 text-[${colors.accent}] border-[${colors.accent}]/20`,
    low: `bg-[${colors.secondary}]/10 text-[${colors.secondary}] border-[${colors.secondary}]/20`,
    
    // Default
    default: `bg-gray-100 text-gray-700 border-gray-200`,
  };
  
  return statusColors[status?.toLowerCase()] || statusColors.default;
};

/**
 * Get form input classes with branding
 * @param {Object} branding - The branding context object
 * @param {boolean} hasError - Whether the input has an error
 * @returns {string} - CSS classes for form inputs
 */
export const getInputClasses = (branding, hasError = false) => {
  const colors = getBrandingColors(branding);
  
  if (hasError) {
    return 'w-full px-3 py-2 border border-red-300 rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500';
  }
  
  return `w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring`;
};

/**
 * Get card classes with branding
 * @param {Object} branding - The branding context object
 * @param {string} variant - Card variant (default, elevated, outlined)
 * @returns {string} - CSS classes for cards
 */
export const getCardClasses = (branding, variant = 'default') => {
  const colors = getBrandingColors(branding);
  
  const variants = {
    default: `bg-card rounded-lg shadow-sm border border-border`,
    elevated: `bg-card rounded-lg shadow-md border border-border`,
    outlined: `bg-card rounded-lg border border-[${colors.primary}]/20`,
  };
  
  return variants[variant] || variants.default;
};

/**
 * Get navigation classes with branding
 * @param {Object} branding - The branding context object
 * @param {string} variant - Navigation variant (header, sidebar, tabs)
 * @returns {string} - CSS classes for navigation
 */
export const getNavigationClasses = (branding, variant = 'header') => {
  const colors = getBrandingColors(branding);
  
  const variants = {
    header: `bg-white border-b border-gray-200`,
    sidebar: `bg-white border-r border-gray-200`,
    tabs: `border-b border-gray-200`,
  };
  
  return variants[variant] || variants.header;
};

/**
 * Get modal classes with branding
 * @param {Object} branding - The branding context object
 * @returns {string} - CSS classes for modals
 */
export const getModalClasses = (branding) => {
  const colors = getBrandingColors(branding);
  return `bg-card rounded-lg shadow-xl border border-border`;
};

/**
 * Get table classes with branding
 * @param {Object} branding - The branding context object
 * @param {string} variant - Table variant (default, striped, bordered)
 * @returns {string} - CSS classes for tables
 */
export const getTableClasses = (branding, variant = 'default') => {
  const colors = getBrandingColors(branding);
  
  const variants = {
    default: `min-w-full divide-y divide-gray-200`,
    striped: `min-w-full divide-y divide-gray-200`,
    bordered: `min-w-full border border-gray-200`,
  };
  
  return variants[variant] || variants.default;
};

/**
 * Apply branding styles to a component
 * @param {Object} branding - The branding context object
 * @param {string} componentType - Type of component (button, input, card, etc.)
 * @param {Object} options - Additional options for styling
 * @returns {Object} - Object with CSS classes and styles
 */
export const applyBranding = (branding, componentType, options = {}) => {
  const colors = getBrandingColors(branding);
  const cssVars = getBrandingCSSVars(branding);
  
  switch (componentType) {
    case 'button':
      return {
        className: getButtonClasses(options.variant, branding),
        style: cssVars,
      };
    case 'input':
      return {
        className: getInputClasses(branding, options.hasError),
        style: cssVars,
      };
    case 'card':
      return {
        className: getCardClasses(branding, options.variant),
        style: cssVars,
      };
    case 'status':
      return {
        className: getStatusClasses(options.status, options.type, branding),
        style: cssVars,
      };
    case 'navigation':
      return {
        className: getNavigationClasses(branding, options.variant),
        style: cssVars,
      };
    case 'modal':
      return {
        className: getModalClasses(branding),
        style: cssVars,
      };
    case 'table':
      return {
        className: getTableClasses(branding, options.variant),
        style: cssVars,
      };
    default:
      return {
        className: '',
        style: cssVars,
      };
  }
};

export default {
  getBrandingColors,
  getBrandingCSSVars,
  getBrandingClasses,
  getButtonClasses,
  getStatusClasses,
  getInputClasses,
  getCardClasses,
  getNavigationClasses,
  getModalClasses,
  getTableClasses,
  applyBranding,
};
